import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { absoluteTime, dimension, formatNumber, formatPos, relativeTime, shortUuid, splitItemId } from '../lib/format';

/** Darstellungsbausteine für technische Werte (design.md §3, §7). */

/** Mono-Wert mit Kopier-Button (bei Hover/Fokus sichtbar, „Kopiert“ für 1,5 s). */
export function Mono({ value, display, copy = true }: { value: string; display?: string; copy?: boolean }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  const text = <span className="mono" title={display && display !== value ? value : undefined}>{display ?? value}</span>;
  if (!copy) return text;
  return (
    <span className="copy">
      {text}
      <button
        type="button"
        aria-label={copied ? 'Kopiert' : `${value} kopieren`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          void navigator.clipboard?.writeText(value).then(() => setCopied(true));
        }}
      >
        {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
        {copied && <span>Kopiert</span>}
      </button>
    </span>
  );
}

/** UUID gekürzt, voll im Tooltip und beim Kopieren. */
export const Uuid = ({ value }: { value: string }) => <Mono value={value} display={shortUuid(value)} />;

/** `minecraft:`**diamond**: Namespace leise, Name in voller Textfarbe. */
export function ItemId({ id }: { id: string }) {
  const { ns, name } = splitItemId(id);
  return (
    <span className="mono" title={id}>
      <span className="ns">{ns}</span>
      {name}
    </span>
  );
}

export function DimMark({ id }: { id: string }) {
  const d = dimension(id);
  return <span className={`dim ${d.kind}${d.kind === 'other' ? ' mono' : ''}`}>{d.label}</span>;
}

/** Position mit Dimensionsmarke davor: `▪ Oberwelt 120 / 64 / -33`. */
export function Position({ dimension: dim, pos }: { dimension: string; pos: [number, number, number] }) {
  return (
    <span className="pos">
      <DimMark id={dim} /> <span className="mono">{formatPos(pos)}</span>
    </span>
  );
}

export const Num = ({ value }: { value: number }) => <>{formatNumber(value)}</>;

/** Relativ in Listen, absolut im Tooltip; `absolute` für Detailansichten. */
export function Time({ iso, absolute = false }: { iso: string | null | undefined; absolute?: boolean }) {
  if (!iso) return <span className="muted">–</span>;
  return (
    <time dateTime={iso} title={absolute ? undefined : absoluteTime(iso)}>
      {absolute ? absoluteTime(iso) : relativeTime(iso)}
    </time>
  );
}

type Tone = 'ok' | 'warn' | 'err' | 'neutral';

/** Status-Tag: Farbe trägt nie allein Bedeutung, der Text steht immer dabei. */
export const Tag = ({ tone, children }: { tone: Tone; children: React.ReactNode }) => (
  <span className={`tag tag-${tone}`}>{children}</span>
);

const USER_STATUS: Record<string, [Tone, string]> = {
  active: ['ok', 'aktiv'],
  pending_beta: ['warn', 'Beta ausstehend'],
  suspended: ['err', 'gesperrt'],
};
export function UserStatusTag({ status }: { status: string }) {
  const [tone, label] = USER_STATUS[status] ?? ['neutral', status];
  return <Tag tone={tone}>{label}</Tag>;
}

export const VerifiedTag = ({ verified }: { verified: boolean }) =>
  verified ? <Tag tone="ok">verifiziert</Tag> : <Tag tone="neutral">nicht verifiziert</Tag>;

/** Definitionsliste statt Karten (design.md §6). */
export function DefList({ items }: { items: [React.ReactNode, React.ReactNode][] }) {
  return (
    <dl className="props">
      {items.map(([k, v], i) => (
        <div key={i} className="prop">
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
